import { sdkTypes } from '@vansite/ts-sharetribe-flex-sdk';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';

import { FacebookCustomEvent } from '../../analytics/facebook';
import { FirebaseCustomEvent } from '../../analytics/firebase';
import config from '../../config';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { notifyMailContactEvent } from '../../ducks/mailContact.duck';
import { createStripeAccount, fetchStripeAccount, updateStripeAccount } from '../../ducks/stripeConnectAccount.duck';
import { saveTranslations } from '../../ducks/translations.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { campingGroundSync, translateText } from '../../util/api';
import { periodTextsForTranslation } from '../../util/campingPeriods';
import { buildCustomLineItemCode } from '../../util/customLineItemCode';
import { storableError } from '../../util/errors';
import { logError } from '../../util/log';
import { replaceUmlauts } from '../../util/text';

const { UUID } = sdkTypes;

const requestAction = (actionType) => (params) => ({ type: actionType, payload: { params } });

const successAction = (actionType) => (result) => ({ type: actionType, payload: result.data });

const errorAction = (actionType) => (error) => ({ type: actionType, payload: error, error: true });

// ================ Action types ================ //

export const MARK_TAB_UPDATED = 'app/EditListingPage/MARK_TAB_UPDATED';
export const CLEAR_UPDATED_TAB = 'app/EditListingPage/CLEAR_UPDATED_TAB';

export const CREATE_LISTING_DRAFT_REQUEST = 'app/EditListingPage/CREATE_LISTING_DRAFT_REQUEST';
export const CREATE_LISTING_DRAFT_SUCCESS = 'app/EditListingPage/CREATE_LISTING_DRAFT_SUCCESS';
export const CREATE_LISTING_DRAFT_ERROR = 'app/EditListingPage/CREATE_LISTING_DRAFT_ERROR';

export const PUBLISH_LISTING_REQUEST = 'app/EditListingPage/PUBLISH_LISTING_REQUEST';
export const PUBLISH_LISTING_SUCCESS = 'app/EditListingPage/PUBLISH_LISTING_SUCCESS';
export const PUBLISH_LISTING_ERROR = 'app/EditListingPage/PUBLISH_LISTING_ERROR';

export const UPDATE_LISTING_REQUEST = 'app/EditListingPage/UPDATE_LISTING_REQUEST';
export const UPDATE_LISTING_SUCCESS = 'app/EditListingPage/UPDATE_LISTING_SUCCESS';
export const UPDATE_LISTING_ERROR = 'app/EditListingPage/UPDATE_LISTING_ERROR';

export const SHOW_LISTINGS_REQUEST = 'app/EditListingPage/SHOW_LISTINGS_REQUEST';
export const SHOW_LISTINGS_SUCCESS = 'app/EditListingPage/SHOW_LISTINGS_SUCCESS';
export const SHOW_LISTINGS_ERROR = 'app/EditListingPage/SHOW_LISTINGS_ERROR';

export const UPLOAD_IMAGE_REQUEST = 'app/EditListingPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/EditListingPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/EditListingPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_IMAGE_ORDER = 'app/EditListingPage/UPDATE_IMAGE_ORDER';

export const REMOVE_LISTING_IMAGE = 'app/EditListingPage/REMOVE_LISTING_IMAGE';

export const SAVE_PAYOUT_DETAILS_REQUEST = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR = 'app/EditListingPage/SAVE_PAYOUT_DETAILS_ERROR';

export const TRANSLATE_FIELDS_REQUEST = 'app/EditListingPage/TRANSLATE_FIELDS_REQUEST';

// ================ Reducer ================ //

/** @type {import('../../../types/vansite').VanSite.State.EditListingPage} */
const initialState = {
  // Error instance placeholders for each endpoint
  createListingDraftError: null,
  publishingListing: null,
  publishListingError: null,
  updateListingError: null,
  showListingsError: null,
  uploadImageError: null,
  createListingDraftInProgress: false,
  submittedListingId: null,
  redirectToListing: false,
  images: {},
  imageOrder: [],
  removedImageIds: [],
  listingDraft: null,
  updatedTab: null,
  updateInProgress: false,
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
};

/**
 *
 * @param state
 * @param action
 */
export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case MARK_TAB_UPDATED:
      return { ...state, updatedTab: payload };
    case CLEAR_UPDATED_TAB:
      return { ...state, updatedTab: null, updateListingError: null };

    case CREATE_LISTING_DRAFT_REQUEST:
      return {
        ...state,
        createListingDraftInProgress: true,
        createListingDraftError: null,
        submittedListingId: null,
        listingDraft: null,
      };

    case CREATE_LISTING_DRAFT_SUCCESS:
      return {
        ...state,
        createListingDraftInProgress: false,
        submittedListingId: payload.data.id,
        listingDraft: payload.data,
      };
    case CREATE_LISTING_DRAFT_ERROR:
      return {
        ...state,
        createListingDraftInProgress: false,
        createListingDraftError: payload,
      };

    case PUBLISH_LISTING_REQUEST:
      return {
        ...state,
        publishingListing: payload.listingId,
        publishListingError: null,
      };
    case PUBLISH_LISTING_SUCCESS:
      return {
        ...state,
        redirectToListing: true,
        publishingListing: null,
        createListingDraftError: null,
        updateListingError: null,
        showListingsError: null,
        uploadImageError: null,
        createListingDraftInProgress: false,
        updateInProgress: false,
      };
    case PUBLISH_LISTING_ERROR: {
      return {
        ...state,
        publishingListing: null,
        publishListingError: {
          listingId: state.publishingListing,
          error: payload,
        },
      };
    }

    case UPDATE_LISTING_REQUEST:
      return { ...state, updateInProgress: true, updateListingError: null };
    case UPDATE_LISTING_SUCCESS:
      return { ...state, updateInProgress: false };
    case UPDATE_LISTING_ERROR:
      return { ...state, updateInProgress: false, updateListingError: payload };

    case SHOW_LISTINGS_REQUEST:
      return { ...state, showListingsError: null };
    case SHOW_LISTINGS_SUCCESS:
      const imagesFromPayload = payload.included
        .filter((include) => include.type === 'image')
        .reduce((acc, image) => {
          acc[image.id.uuid] = image;
          return acc;
        }, {});
      const images = {
        ...imagesFromPayload,
      };
      const imagesMetadata = payload.data.attributes?.publicData?.imagesMetadata;
      const imageOrderFromMetadata =
        imagesMetadata && Array.isArray(imagesMetadata)
          ? imagesMetadata.map((meta) => meta.id)
          : [];
      const imagesNotOrdered = Object.keys(images).filter(
        (id) => !imageOrderFromMetadata.includes(id),
      );
      const imageOrder = [...imageOrderFromMetadata, ...imagesNotOrdered];
      return { ...initialState, images, imageOrder };

    case SHOW_LISTINGS_ERROR:
      return { ...state, showListingsError: payload, redirectToListing: false };

    case UPLOAD_IMAGE_REQUEST: {
      // payload.params: { id: 'tempId', file }
      const images = {
        ...state.images,
        [payload.params.id]: { ...payload.params },
      };
      return {
        ...state,
        images,
        imageOrder: state.imageOrder.concat([payload.params.id]),
        uploadImageError: null,
      };
    }
    case UPLOAD_IMAGE_SUCCESS: {
      // payload.params: { id: 'tempId', imageId: 'some-real-id'}
      const { id, imageId } = payload;
      const existingImage = state.images[id];
      // The temp image entry may have been removed before the upload resolved.
      if (!existingImage) {
        return state;
      }
      const images = { ...state.images, [id]: { id, imageId, file: existingImage.file } };
      return { ...state, images };
    }
    case UPLOAD_IMAGE_ERROR: {

      const { id, error } = payload;
      const imageOrder = state.imageOrder.filter((i) => i !== id);
      const images = omit(state.images, id);
      return { ...state, imageOrder, images, uploadImageError: error };
    }
    case UPDATE_IMAGE_ORDER:
      return { ...state, imageOrder: payload.imageOrder };

    case REMOVE_LISTING_IMAGE: {
      const id = payload.imageId;

      // Only mark the image removed if it hasn't been added to the
      // listing already
      const removedImageIds = state.removedImageIds.includes(id)
        ? state.removedImageIds
        : state.removedImageIds.concat(id);

      // Always remove from the draft since it might be a new image to
      // an existing listing.
      const images = omit(state.images, id);
      const imageOrder = state.imageOrder.filter((i) => i !== id);

      return { ...state, images, imageOrder, removedImageIds };
    }

    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: true };

    case TRANSLATE_FIELDS_REQUEST:
      return { ...state };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const markTabUpdated = (tab) => ({
  type: MARK_TAB_UPDATED,
  payload: tab,
});

export const clearUpdatedTab = () => ({
  type: CLEAR_UPDATED_TAB,
});

export const updateImageOrder = (imageOrder) => ({
  type: UPDATE_IMAGE_ORDER,
  payload: { imageOrder },
});

export const removeListingImage = (imageId) => ({
  type: REMOVE_LISTING_IMAGE,
  payload: { imageId },
});

// All the action creators that don't have the {Success, Error} suffix
// take the params object that the corresponding SDK endpoint method
// expects.

// SDK method: ownListings.create
export const createListingDraft = requestAction(CREATE_LISTING_DRAFT_REQUEST);
export const createListingDraftSuccess = successAction(CREATE_LISTING_DRAFT_SUCCESS);
export const createListingDraftError = errorAction(CREATE_LISTING_DRAFT_ERROR);

// SDK method: ownListings.publish
export const publishListing = requestAction(PUBLISH_LISTING_REQUEST);
export const publishListingSuccess = successAction(PUBLISH_LISTING_SUCCESS);
export const publishListingError = errorAction(PUBLISH_LISTING_ERROR);

// SDK method: ownListings.update
export const updateListing = requestAction(UPDATE_LISTING_REQUEST);
export const updateListingSuccess = successAction(UPDATE_LISTING_SUCCESS);
export const updateListingError = errorAction(UPDATE_LISTING_ERROR);

// SDK method: ownListings.show
export const showListings = requestAction(SHOW_LISTINGS_REQUEST);
export const showListingsSuccess = successAction(SHOW_LISTINGS_SUCCESS);
export const showListingsError = errorAction(SHOW_LISTINGS_ERROR);

// SDK method: images.upload
export const uploadImage = requestAction(UPLOAD_IMAGE_REQUEST);
export const uploadImageSuccess = successAction(UPLOAD_IMAGE_SUCCESS);
export const uploadImageError = errorAction(UPLOAD_IMAGE_ERROR);

export const savePayoutDetailsRequest = requestAction(SAVE_PAYOUT_DETAILS_REQUEST);
export const savePayoutDetailsSuccess = successAction(SAVE_PAYOUT_DETAILS_SUCCESS);
export const savePayoutDetailsError = errorAction(SAVE_PAYOUT_DETAILS_ERROR);

// ================ Thunk ================ //

/**
 * @param {object} actionPayload
 * @returns {Promise<object>}
 */
export function requestShowListing(actionPayload) {
  return (dispatch, getState, sdk) => {
    dispatch(showListings(actionPayload));
    return sdk.ownListings
      .show(actionPayload)
      .then((response) => {
        // EditListingPage fetches new listing data, which also needs to be added to global data
        dispatch(addMarketplaceEntities(response));
        // In case of success, we'll clear state.EditListingPage (user will be redirected away)
        dispatch(showListingsSuccess(response));
        return response;
      })
      .catch((e) => {
        logError(e, 'show-listing-failed', { listingId: actionPayload.id });
        dispatch(showListingsError(storableError(e)));
      });
  };
}

/**
 *
 * @param data
 */
export function requestCreateListingDraft(data) {
  return (dispatch, getState, sdk) => {
    dispatch(createListingDraft(data));

    const queryParams = {
      expand: true,
      include: ['author', 'images'],
      'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
    };

    // default values
    data = {
      ...data,
      publicData: {
        ...data.publicData,
        ...config.listing.publicData,
      },
      privateData: {
        ...data.privateData,
        ...config.listing.privateData,
      },
    };

    return sdk.ownListings
      .createDraft(data, queryParams)
      .then((response) => {
        //const id = response.data.data.id.uuid;

        // Add the created listing to the marketplace data
        dispatch(addMarketplaceEntities(response));

        // Modify store to understand that we have created listing and can redirect away
        dispatch(createListingDraftSuccess(response));

        dispatch(
          saveTranslations({
            id: response.data.data.id.uuid,
            type: 'listing',
            content: {
              title: data.title,
              description: data.description,
              rules: data.publicData.rules,
              customLineItems: data.publicData?.customLineItems,
            },
          }),
        );
        dispatch(notifyMailContactEvent('listing-draft-created'));
        return response;
      })
      .catch((e) => {
        logError(e, 'create-listing-draft-failed', { listingData: data });
        return dispatch(createListingDraftError(storableError(e)));
      });
  };
}

export const requestPublishListingDraft = (listingId) => async (dispatch, getState, sdk) => {
  const { currentUser } = getState().user;

  dispatch(publishListing(listingId));

  try {
    const apiResponse = await sdk.ownListings.publishDraft({ id: listingId }, { expand: true });

    dispatch(addMarketplaceEntities(apiResponse));
    dispatch(publishListingSuccess(apiResponse));

    FirebaseCustomEvent.withCustomName('create_listing').logEvent();
    FacebookCustomEvent.withCurrentUserUUID(currentUser?.id?.uuid)
      .withCustomName('CreateListing')
      .withEventID(listingId)
      .logEvent();

    const analyticsAndMailChanges = async () => {
      const apiQueryResponse = await sdk.ownListings.query({});
      const listings = apiQueryResponse.data.data;
      if (listings.length === 1) {
        FirebaseCustomEvent.withCustomName('become_host').logEvent();
        FacebookCustomEvent.withCurrentUserUUID(currentUser?.id?.uuid)
          .withCustomName('BecomeHost')
          .withEventID(listingId)
          .logEvent();
      }
      dispatch(notifyMailContactEvent('listing-published'));
    };

    analyticsAndMailChanges();

    return apiResponse;
  } catch (e) {
    logError(e, 'publish-listing-draft-failed', { listingId });
    dispatch(publishListingError(storableError(e)));
  }
};

/**
 * Images return imageId which we need to map with previously generated temporary id
 * @param {object} actionPayload { id: string, file: File }
 * @returns {Promise<object>}
 */
export function requestImageUpload(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImage(actionPayload));
    return sdk.images
      .upload({ image: actionPayload.file })
      .then((resp) => dispatch(uploadImageSuccess({ data: { id, imageId: resp.data.data.id } })))
      .catch((e) => {
        logError(e, 'image-upload-failed', { listingId: id });
        dispatch(uploadImageError({ id, error: storableError(e) }));
      });
  };
}

// Update the given tab of the wizard with the given data. This saves
// the data to the listing, and marks the tab updated so the UI can
// display the state.
export const requestUpdateListing = (tab, data) => async (dispatch, getState, sdk) => {
  dispatch(updateListing(data));
  const { id } = data;
  let updateResponse;

  const hasCustomLineItems = Array.isArray(data.publicData?.customLineItems);
  // Recurring closing periods only block bookings once they are materialised
  // into availability exceptions, which normally happens in the nightly
  // camping-ground sync. Saving the tab triggers that sync right away.
  // An empty array counts: it has to clear previously materialised blocks.
  const savesClosedPeriods = !!data.publicData && 'closedPeriods' in data.publicData;
  const savesSeasonalPrices = !!data.publicData && 'seasonalPrices' in data.publicData;
  const currentListing = getState().marketplaceData.entities.ownListing[id.uuid];

  if (hasCustomLineItems) {
    let customLineItems = {};
    for (const customLineItem of data.publicData.customLineItems) {
      const { description, price, title, type } = customLineItem;
      const enTitle = (await translateText({ text: replaceUmlauts(title), targetLanguage: 'en' }))
        .translatedText;
      const lineItemCode = buildCustomLineItemCode(enTitle);

      customLineItems[lineItemCode] = {
        price: { amount: price.amount, currency: price.currency },
        type,
        title,
        description,
      };
    }
    if (!isEmpty(customLineItems)) {
      data.publicData.customLineItems = customLineItems;
    } else {
      data.publicData.customLineItems = null;
    }
  }

  if (
    data.publicData &&
    data.publicData.amountOfSeats &&
    currentListing.attributes.availabilityPlan
  ) {
    const newAvailabilityPlan = currentListing.attributes.availabilityPlan;
    newAvailabilityPlan.entries = currentListing.attributes.availabilityPlan.entries.map(
      (entry) => {
        entry.seats = data.publicData.amountOfSeats;
        return entry;
      },
    );
    data.availabilityPlan = newAvailabilityPlan;
  }

  if (data.publicData?.cancellationPolicyTier) {
    data.publicData.cancellationPolicy = null;
  }

  // Host free texts on recurring camping-ground periods, keyed by their range so a
  // reordered list does not re-translate. An empty map is meaningful: it prunes the
  // stored texts once the host clears the last label/note.
  const seasonalPriceTexts = savesSeasonalPrices
    ? periodTextsForTranslation(data.publicData.seasonalPrices, 'label')
    : null;
  const closedPeriodTexts = savesClosedPeriods
    ? periodTextsForTranslation(data.publicData.closedPeriods, 'note')
    : null;

  const hasTranslatableContent =
    data.description ||
    data.title ||
    data.publicData?.rules ||
    hasCustomLineItems ||
    !!seasonalPriceTexts ||
    !!closedPeriodTexts;

  if (hasTranslatableContent) {
    dispatch(
      saveTranslations({
        type: 'listing',
        id: currentListing.id.uuid,
        content: {
          title: data.title,
          description: data.description,
          rules: data.publicData?.rules,
          // Only send the maps this tab actually owns. `put.js` prunes every key
          // missing from a map it receives, so an unrelated tab passing `{}` would
          // wipe translations it knows nothing about.
          ...(hasCustomLineItems
            ? { customLineItems: data.publicData.customLineItems || {} }
            : {}),
          ...(seasonalPriceTexts ? { seasonalPrices: seasonalPriceTexts } : {}),
          ...(closedPeriodTexts ? { closedPeriods: closedPeriodTexts } : {}),
        },
      }),
    );
  }

  return sdk.ownListings
    .update(data, { expand: true })
    .then((response) => {
      updateResponse = response;
      const payload = {
        id,
        include: ['author', 'images'],
        'fields.image': [
          'variants.landscape-crop',
          'variants.landscape-crop2x',
          'variants.landscape-crop4x',
        ],
      };
      return dispatch(requestShowListing(payload));
    })
    .then(() => {
      dispatch(markTabUpdated(tab));
      dispatch(updateListingSuccess(updateResponse));
      if (savesClosedPeriods) {
        // Fire and forget: the nightly run self-heals, so a failing sync must
        // neither fail the save nor block the wizard navigation.
        campingGroundSync({ listingId: id.uuid }).catch((e) => {
          logError(e, 'camping-ground-sync-failed', { listingId: id.uuid });
        });
      }
      return updateResponse;
    })
    .catch((e) => {
      logError(e, 'update-listing-failed', { listingData: data });
      return dispatch(updateListingError(storableError(e)));
    });
};

export const savePayoutDetails = (values, isUpdateCall) => (dispatch) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  dispatch(savePayoutDetailsRequest());

  return dispatch(upsertThunk(values))
    .then((response) => {
      dispatch(savePayoutDetailsSuccess());
      return response;
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};

// loadData is run for each tab of the wizard. When editing an
// existing listing, the listing must be fetched first.
export const loadData = (params) => (dispatch, getState) => {
  dispatch(clearUpdatedTab());
  const { id, type } = params;

  if (type === 'new') {
    // No need to listing data when creating a new listing
    return Promise.all([dispatch(fetchCurrentUser())])
      .then((response) => {
        const currentUser = getState().user.currentUser;
        if (currentUser && currentUser.stripeAccount) {
          dispatch(fetchStripeAccount());
        }
        return response;
      })
      .catch((e) => {
        throw e;
      });
  }

  const payload = {
    id: new UUID(id),
    include: ['author', 'images'],
    'fields.image': [
      'variants.landscape-crop',
      'variants.landscape-crop2x',
      'variants.landscape-crop4x',
    ],
  };

  return Promise.all([dispatch(requestShowListing(payload)), dispatch(fetchCurrentUser())])
    .then((response) => {
      const currentUser = getState().user.currentUser;
      if (currentUser && currentUser.stripeAccount) {
        dispatch(fetchStripeAccount());
      }
      return response;
    })
    .catch((e) => {
      throw e;
    });
};
